import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TermStatement } from "../../../api/content/ontologyApi";
import { SelectionToChat, type ChatReference } from "../../chatReferences";
import { InlineTextEdit, type EditSession } from "../../editing";
import Chip from "../../../ui/feedback/Chip";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { TermEdit } from "../proposals/termPatches";
import { compactIri } from "../viewer/types";
import { PREDICATE_NAME_KEYS, reflowText } from "./statementText";
import { addedStatements, isAddedKey, requestFor, stagedLanguage, stagedText, stageStatement, statementKey, type StatementSection } from "./termEditing";

/** Editing a section's statements in place, where the user may propose changes to the term (F96). */
export interface StatementEditing {
  readonly section: StatementSection;
  readonly session: EditSession<TermEdit>;
  readonly termIri: string;
  /** Whether the project can change this statement: only its own modules' are. */
  readonly isEditable: (statement: TermStatement) => boolean;
  /** Selecting a statement's text asks about the term, quoting it, or edits the statement. */
  readonly projectId: string;
  readonly subject: Omit<ChatReference, "quote">;
}

interface TermStatementListProps {
  statements: readonly TermStatement[];
  emptyText: string;
  /** The properties the section stands for: statements by them don't name their property. */
  usualPredicates: ReadonlySet<string>;
  editing?: StatementEditing | undefined;
}

/** Labels or definitions, reflowed, each with its language and, if not the usual one, the
 * property that states it, by name with the IRI on hover. Where the user may propose changes,
 * each of the project's own is edited in place, and new ones are added below. */
export default function TermStatementList({ statements, emptyText, usualPredicates, editing }: TermStatementListProps) {
  const { t } = useTranslation();
  const added = editing === undefined ? [] : addedStatements(editing.session, editing.section);
  // A new statement being written, before it is kept.
  const openKey = editing?.session.openKey ?? null;
  const newKey = editing !== undefined && openKey !== null && isAddedKey(openKey, editing.section) && !added.some((staged) => staged.key === openKey) ? openKey : null;
  if (statements.length === 0 && added.length === 0 && newKey === null) {
    return <p className="app-text-muted text-sm">{emptyText}</p>;
  }
  const aside = (statement: TermStatement): ReactNode => {
    const predicateKey = PREDICATE_NAME_KEYS[statement.predicate];
    return (
      <>
        {statement.language !== null ? <Chip>{statement.language}</Chip> : null}
        {usualPredicates.has(statement.predicate) ? null : (
          <span className="app-text-muted text-xs" title={statement.predicate}>
            {predicateKey !== undefined ? t(`ontologyViewer.predicates.${predicateKey}`) : compactIri(statement.predicate)}
          </span>
        )}
      </>
    );
  };
  return (
    <ul className="space-y-1.5">
      {statements.map((statement) => {
        const key = statementKey(statement);
        if (editing === undefined || !editing.isEditable(statement)) {
          return (
            <li key={key} className="app-text flex flex-wrap items-start gap-2 text-sm">
              <span className="min-w-0 flex-1 whitespace-pre-line break-words">{reflowText(statement.text)}</span>
              {aside(statement)}
            </li>
          );
        }
        return (
          <li key={key} className="app-text text-sm">
            <StatementEditor editing={editing} itemKey={key} statement={statement} aside={aside(statement)} />
          </li>
        );
      })}
      {/* One list, so a new statement keeps its field once its first letter makes it an edit. */}
      {editing === undefined
        ? null
        : [...added.map((staged) => staged.key), ...(newKey === null ? [] : [newKey])].map((key) => (
            <li key={key} className="app-text text-sm">
              <StatementEditor editing={editing} itemKey={key} statement={null} aside={languageChip(stagedLanguage(editing.session.stagedOf(key)?.edit))} />
            </li>
          ))}
    </ul>
  );
}

const languageChip = (language: string | null): ReactNode => (language === null ? null : <Chip>{language}</Chip>);

interface StatementEditorProps {
  editing: StatementEditing;
  itemKey: string;
  /** `null` for a new statement. */
  statement: TermStatement | null;
  aside: ReactNode;
}

function StatementEditor({ editing, itemKey, statement, aside }: StatementEditorProps) {
  const { t, i18n } = useTranslation();
  const { section, session, termIri } = editing;
  const staged = session.stagedOf(itemKey);
  // A changed statement keeps its language. A new one takes its edit's, so Cancel restores it
  // with the text; before its first letter, the language typed so far.
  const [draftLanguage, setDraftLanguage] = useState(i18n.language.split("-")[0] ?? "en");
  const language = staged === undefined ? draftLanguage : (stagedLanguage(staged.edit) ?? "");
  const label = t(section === "label" ? "ontologyViewer.proposals.labelText" : "ontologyViewer.proposals.definitionText");
  const stage = (text: string, inLanguage: string) => {
    stageStatement(session, itemKey, requestFor(section, statement), text, statement?.language ?? inLanguage, termIri);
  };
  const editor = (
    <InlineTextEdit
      value={statement?.text ?? ""}
      staged={stagedText(staged?.edit)}
      problem={staged?.problem == null ? null : t(`ontologyViewer.proposals.problems.${staged.problem}`)}
      editing={session.openKey === itemKey}
      onOpen={() => { session.open(itemKey); }}
      onClose={session.close}
      onCancel={session.cancel}
      onChange={(text) => { stage(text, language); }}
      onUnstage={() => { session.unstage(itemKey); }}
      label={label}
      multiline={section === "definition"}
      readOnly={session.saving.status === "submitting"}
      aside={aside}
      editorAside={
        statement === null ? (
          <input
            aria-label={t("ontologyViewer.proposals.language")}
            title={t("ontologyViewer.proposals.languageHint")}
            className={`${FORM_COMPACT_CONTROL_CLASS} max-w-[5rem]`}
            value={language}
            onChange={(event) => {
              const text = stagedText(staged?.edit);
              if (text === undefined) setDraftLanguage(event.target.value);
              else stage(text, event.target.value);
            }}
          />
        ) : null
      }
    >
      {statement === null ? null : reflowText(statement.text)}
    </InlineTextEdit>
  );
  return statement === null ? (
    editor
  ) : (
    <SelectionToChat projectId={editing.projectId} subject={editing.subject} onEdit={() => { session.open(itemKey); }}>
      {editor}
    </SelectionToChat>
  );
}
